import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Download,
  ExternalLink,
  History,
  RotateCw,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { startReportDownload } from "@/components/reports/report-item/download";
import { ReportItem } from "@/components/reports/report-item/report-item";
import { formatLabel } from "@/components/reports/report-item/report-format";
import { ReportProgressBar } from "@/components/reports/report-item/report-progress-bar";
import { useReportProgress } from "@/components/reports/report-item/use-report-progress";
import { cn } from "@/lib/utils";
import type {
  CollectionExport,
  CollectionKind,
  ProjectReport,
  ReportFormat,
} from "@/types/api";

import { useCollectionExports } from "./use-collection-exports";

interface CollectionExportsSectionProps {
  projectId: string;
  collectionId: string;
  /** What the report holds, for the running bar's phase wording. */
  kind: CollectionKind;
  /** The collection's newest export. Drives the row's state. */
  latestExport: CollectionExport | null;
  /**
   * Start a new export in the given format. Omitted when the user cannot
   * export, which hides Retry on a failed row.
   */
  onRetry?: (format: ReportFormat) => void;
  /** An export request is in flight; Retry waits for it. */
  retrying?: boolean;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/**
 * A report's past exports, collapsed to one row above its contents — and the
 * one slot an export's whole lifecycle plays out in: running, ready, failed.
 *
 * Placement is the whole point. These used to live behind the overflow menu in
 * a sheet — two taps and a hidden menu to reach a file the header was already
 * naming in dead text. One row, always visible, directly under the header:
 * above the contents because a 200-photo report makes anything below the grid
 * unreachable, and collapsed because the contents are what people came for.
 *
 * See [[collection-exports-section.spec]].
 */
export function CollectionExportsSection({
  projectId,
  collectionId,
  kind,
  latestExport,
  onRetry,
  retrying = false,
}: CollectionExportsSectionProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  // The list only keeps itself current while somebody is looking at it. The
  // row reads nothing from this query that moves during an export — its state
  // comes from the collection's own polled `latest_export`, and the count only
  // changes on a create, which already invalidates. Polling it while collapsed
  // doubles the request rate on this page for a panel nobody has opened.
  const { data, isLoading, isError, refetch } = useCollectionExports(
    projectId,
    collectionId,
    { poll: open },
  );

  const entries = data?.exports ?? [];
  const count = data?.count ?? 0;
  const label = `Exports${count > 0 ? ` · ${count}` : ""}`;
  const toggle = {
    open,
    panelId,
    label,
    onToggle: () => setOpen((prev) => !prev),
  };

  const panel = open && (
    <div id={panelId} className="flex flex-col gap-2 border-t p-3">
      {isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))
      ) : isError ? (
        // Distinct from Empty, deliberately. A report is born from an
        // export, so it always has at least one — "No exports yet." after a
        // dropped request would tell a user their client's file never
        // existed, while the row above it still reads `Latest 10 Sep`.
        <div className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <span>Couldn&rsquo;t load previous exports</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : entries.length === 0 ? (
        <p className="py-2 text-center text-sm text-muted-foreground">
          No exports yet.
        </p>
      ) : (
        entries.map((entry) => (
          <ReportItem
            key={entry.id}
            report={toReportShape(entry)}
            kind={kind}
            creatorName={creatorName(entry)}
            canRename={false}
            canDelete={false}
            onRename={() => {}}
            onDelete={() => {}}
          />
        ))
      )}
    </div>
  );

  if (!latestExport) {
    return (
      <ExportsCard tone="idle" toggle={toggle}>
        {panel}
      </ExportsCard>
    );
  }

  return (
    // Keyed, and load-bearing. This is a singleton whose export id mutates
    // under it — unlike the report rows, which live in a list keyed by report
    // id and so never see a second report. Without the key React reuses the
    // instance across an export change, and `useReportProgress` keeps the
    // previous export's start time (its ref is only ever seeded from null) and
    // its latched `isDone`: the second export opens pegged at 90% and never
    // plays its 90→100% handoff. The key also ends the row's memory of having
    // watched an export, which is what retires a Ready row when the next
    // export starts.
    <LatestExportRow
      key={latestExport.id}
      entry={latestExport}
      kind={kind}
      toggle={toggle}
      onRetry={onRetry}
      retrying={retrying}
    >
      {panel}
    </LatestExportRow>
  );
}

interface ToggleProps {
  open: boolean;
  panelId: string;
  label: string;
  onToggle: () => void;
}

/**
 * The row for the collection's newest export: works out which of the four
 * states it is in and fills the card for it.
 *
 * Mounted for as long as the collection has an export at all, not only while
 * one is running: the hook has to survive the PROCESSING → COMPLETED flip to
 * play its 90→100% handoff, and the row has to remember it watched the job to
 * land on Ready afterwards.
 */
function LatestExportRow({
  entry,
  kind,
  toggle,
  onRetry,
  retrying,
  children,
}: {
  entry: CollectionExport;
  kind: CollectionKind;
  toggle: ToggleProps;
  onRetry?: (format: ReportFormat) => void;
  retrying: boolean;
  children: ReactNode;
}) {
  // Keyed by the **export** id, never the collection's: a collection produces
  // many exports, and a collection-keyed start time would resume the previous
  // export's curve — a bar that opens at 80% on a job that just started.
  //
  // Calibrated by the export's **frozen** `item_count`, not the collection's
  // live count. Two reasons: the panel row for this same export already uses
  // it, so the two bars on screen cannot disagree about how long the job is;
  // and adding photos mid-export would otherwise change the duration under a
  // running simulation, which restarts the tick and walks progress backwards.
  const progress = useReportProgress(
    entry.id,
    entry.status,
    entry.item_count,
    kind,
  );

  const statusInProgress =
    entry.status === "PENDING" || entry.status === "PROCESSING";

  // "Watched" is what earns a Ready row: this page saw the export running. An
  // export already COMPLETED when the page loaded is not news, so it lands on
  // idle. Adjusted during render, not in an effect, so the first COMPLETED
  // render after a watched run already knows — the React-documented way to
  // derive state from a changing prop.
  const [watched, setWatched] = useState(statusInProgress);
  if (statusInProgress && !watched) setWatched(true);
  const [opened, setOpened] = useState(false);

  // Hold the bar through the completion animation, exactly as the report row
  // does — only FAILED drops it immediately. Gated on `watched` so a row that
  // mounts on an already-COMPLETED export does not flash a bar at 0% for the
  // one render before the hook reports `isDone`.
  const running =
    statusInProgress ||
    (watched && entry.status === "COMPLETED" && !progress.isDone);
  const failed = entry.status === "FAILED";
  const ready =
    watched &&
    entry.status === "COMPLETED" &&
    progress.isDone &&
    !!entry.file &&
    !opened;

  const tone: Tone = running
    ? "running"
    : failed
      ? "failed"
      : ready
        ? "ready"
        : "idle";

  // The toast is the nudge for whoever happens to be looking; the row is how
  // they act on it. Once per watched export, and never for a result that was
  // already there when the page loaded.
  const announced = useRef(false);
  useEffect(() => {
    if (!watched || announced.current) return;
    if (tone === "ready") {
      announced.current = true;
      toast("Report ready");
    } else if (tone === "failed") {
      announced.current = true;
      toast("Export failed");
    }
  }, [tone, watched]);

  if (tone === "running") {
    return (
      <ExportsCard tone="running" toggle={toggle} status={<ReportProgressBar progress={progress} />}>
        {children}
      </ExportsCard>
    );
  }

  if (tone === "ready") {
    const format = formatLabel(entry.format);
    const file = entry.file!;
    const opensInline = entry.format === "pdf";
    return (
      <ExportsCard
        tone="ready"
        toggle={toggle}
        status={
          <StatusText
            title="Export ready"
            detail={`${format} · just now`}
          />
        }
        action={
          opensInline ? (
            <Button asChild variant="outline" size="sm">
              <a
                href={file}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${entry.name} (${format})`}
                onClick={() => setOpened(true)}
              >
                <ExternalLink className="size-4" aria-hidden="true" />
                Open
              </a>
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Download ${entry.name} (${format})`}
              onClick={() => {
                startReportDownload(file, entry.name);
                setOpened(true);
              }}
            >
              <Download className="size-4" aria-hidden="true" />
              Open
            </Button>
          )
        }
      >
        {children}
      </ExportsCard>
    );
  }

  if (tone === "failed") {
    return (
      <ExportsCard
        tone="failed"
        toggle={toggle}
        status={
          <StatusText
            title="Export failed"
            detail={entry.error_message || "The file could not be made"}
            destructive
          />
        }
        action={
          onRetry && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={retrying}
              // "That again": the failed export's format, not whatever the
              // picker says now. A different format is what Export is for.
              onClick={() => onRetry(entry.format)}
            >
              <RotateCw className="size-4" aria-hidden="true" />
              Retry
            </Button>
          )
        }
      >
        {children}
      </ExportsCard>
    );
  }

  const latestDate = formatDate(entry.created_at);
  return (
    <ExportsCard
      tone="idle"
      toggle={toggle}
      meta={latestDate ? `Latest ${latestDate}` : null}
    >
      {children}
    </ExportsCard>
  );
}

type Tone = "idle" | "running" | "ready" | "failed";

const TONE_CARD: Record<Tone, string> = {
  idle: "",
  running: "",
  ready: "border-primary ring-1 ring-primary",
  failed: "border-destructive/45",
};

/**
 * The card and its one row. Idle, the whole row is the toggle. In every other
 * state the middle is a status, not a control — only the chevron toggles, so
 * Open and Retry are real buttons rather than buttons nested in a button, and
 * the status's live region is announced rather than swallowed into a button's
 * accessible name.
 */
function ExportsCard({
  tone,
  toggle,
  meta,
  status,
  action,
  children,
}: {
  tone: Tone;
  toggle: ToggleProps;
  meta?: string | null;
  status?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  const chevron = (
    <ChevronDown
      className={cn(
        "size-5 shrink-0 text-muted-foreground transition-transform",
        toggle.open && "rotate-180",
      )}
      aria-hidden="true"
    />
  );
  // Only while the panel is in the DOM: `aria-controls` pointing at an id that
  // does not exist is a dangling IDREF, and `aria-expanded` already tells a
  // collapsed button it has something to open.
  const ariaControls = toggle.open ? toggle.panelId : undefined;

  return (
    <section
      className={cn(
        "overflow-hidden rounded-lg border bg-card",
        TONE_CARD[tone],
      )}
    >
      {tone === "idle" ? (
        <button
          type="button"
          aria-expanded={toggle.open}
          aria-controls={ariaControls}
          onClick={toggle.onToggle}
          className="flex w-full items-center gap-3 p-3 text-left"
        >
          <History
            className="size-5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="shrink-0 text-sm font-medium">{toggle.label}</span>
          {meta && (
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {meta}
            </span>
          )}
          <span className="ml-auto">{chevron}</span>
        </button>
      ) : (
        <div className="flex items-center gap-3 py-2 pl-3 pr-1">
          <ToneGlyph tone={tone} />
          <div className="min-w-0 flex-1">{status}</div>
          {action}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={toggle.label}
            aria-expanded={toggle.open}
            aria-controls={ariaControls}
            onClick={toggle.onToggle}
          >
            {chevron}
          </Button>
        </div>
      )}
      {children}
    </section>
  );
}

function ToneGlyph({ tone }: { tone: Tone }) {
  if (tone === "ready") {
    return (
      <span
        className="grid size-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground"
        aria-hidden="true"
      >
        <Check className="size-3" strokeWidth={3} />
      </span>
    );
  }
  if (tone === "failed") {
    return (
      <AlertCircle
        className="size-5 shrink-0 text-destructive"
        aria-hidden="true"
      />
    );
  }
  return (
    <History
      className="size-5 shrink-0 text-muted-foreground"
      aria-hidden="true"
    />
  );
}

function StatusText({
  title,
  detail,
  destructive = false,
}: {
  title: string;
  detail: string;
  destructive?: boolean;
}) {
  return (
    <div role="status" className="flex min-w-0 flex-col">
      <span
        className={cn(
          "truncate text-sm font-medium",
          destructive && "text-destructive",
        )}
      >
        {title}
      </span>
      <span className="truncate text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

function creatorName(entry: CollectionExport): string {
  if (!entry.created_by) return "Unknown user";
  return (
    `${entry.created_by.first_name} ${entry.created_by.last_name}`.trim() ||
    "Unknown user"
  );
}

/**
 * Present an export as the shape {@link ReportItem} already renders.
 *
 * The version rows are the reports list's rows: same four status variants,
 * same format chip, same open-inline-or-download behaviour. Adapting the
 * payload is cheaper than a second row component that would drift from it.
 */
function toReportShape(entry: CollectionExport): ProjectReport {
  return {
    id: entry.id,
    project: "",
    created_by: entry.created_by?.id ?? "",
    file: entry.file,
    name: entry.name,
    thumbnail: entry.thumbnail,
    status: entry.status,
    format: entry.format,
    error_message: entry.error_message,
    record_count: entry.item_count,
    completed_at: entry.completed_at,
    created_at: entry.created_at,
    updated_at: entry.updated_at,
  };
}
